import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { estimatePrice } from "@/lib/pricing";
import { useFormatPrice } from "@/lib/currency";

export const Route = createFileRoute("/routes")({
  head: () => ({
    meta: [
      { title: "Popular Routes & Prices — Bacha Way Travel" },
      { name: "description", content: "Sample prices for popular airport transfers in Morocco: Casablanca, Marrakech, Fez, Agadir, Tangier and more." },
    ],
  }),
  component: RoutesPage,
});

const popular: Array<{ from: string; to: string; km: number }> = [
  { from: "Casablanca Airport (CMN)", to: "Casablanca city center", km: 30 },
  { from: "Casablanca Airport (CMN)", to: "Rabat", km: 95 },
  { from: "Marrakech Airport (RAK)", to: "Marrakech Medina", km: 6 },
  { from: "Marrakech Airport (RAK)", to: "Essaouira", km: 190 },
  { from: "Marrakech Airport (RAK)", to: "Ouarzazate", km: 200 },
  { from: "Marrakech Airport (RAK)", to: "Casablanca", km: 240 },
  { from: "Fez Airport (FEZ)", to: "Fez Medina", km: 15 },
  { from: "Fez Airport (FEZ)", to: "Chefchaouen", km: 200 },
  { from: "Agadir Airport (AGA)", to: "Agadir city / Taghazout", km: 40 },
  { from: "Tangier Airport (TNG)", to: "Tangier city", km: 15 },
  { from: "Tangier Airport (TNG)", to: "Chefchaouen", km: 115 },
  { from: "Rabat Airport (RBA)", to: "Rabat city", km: 12 },
];

const featured: Array<{ slug: string; title: string; tag: string }> = [
  { slug: "agadir-airport-to-imsouane-surf", title: "Agadir Airport → Imsouane Surf", tag: "Surfers" },
  { slug: "agadir-to-taghazout-surf", title: "Agadir → Taghazout Surf", tag: "Surf & nomads" },
  { slug: "marrakech-to-agadir", title: "Marrakech → Agadir", tag: "City to city" },
  { slug: "marrakech-to-essaouira", title: "Marrakech → Essaouira", tag: "Day trip" },
  { slug: "agadir-airport-to-essaouira", title: "Agadir Airport → Essaouira", tag: "Coast to coast" },
];

function RoutesPage() {
  const fmt = useFormatPrice();
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
      <div className="text-xs uppercase tracking-[0.2em] text-primary">{t("routes.eyebrow")}</div>
      <h1 className="mt-3 font-display text-5xl md:text-6xl">{t("routes.title")}</h1>
      <p className="mt-5 max-w-2xl text-muted-foreground">{t("routes.lead")}</p>



      <div className="mt-12">
        <div className="text-xs uppercase tracking-[0.2em] text-primary">Featured transfer routes</div>
        <h2 className="mt-2 font-display text-2xl md:text-3xl">Dedicated pages for our most-booked corridors</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((f) => (
            <a
              key={f.slug}
              href={`/transfers/${f.slug}`}
              className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/40"
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-primary">{f.tag}</div>
              <h3 className="mt-2 font-display text-lg">{f.title}</h3>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-primary">
                View page & price <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </div>
            </a>
          ))}
        </div>
      </div>

      <div className="mt-16 overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-card text-xs uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-4">{t("routes.thRoute")}</th>
              <th className="text-left px-5 py-4 hidden md:table-cell">{t("routes.thDistance")}</th>
              <th className="text-right px-5 py-4">{t("routes.thFrom")}</th>
              <th className="px-5 py-4" />
            </tr>
          </thead>
          <tbody>
            {popular.map((r, i) => {
              const p = estimatePrice({ vehicleType: "economy", distanceKm: r.km, tripType: "one-way" });
              return (
                <tr key={i} className="border-t border-border hover:bg-card/50">
                  <td className="px-5 py-4">
                    <div className="text-foreground">{r.from}</div>
                    <div className="text-xs text-muted-foreground">→ {r.to}</div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-muted-foreground">{r.km} km</td>
                  <td className="px-5 py-4 text-right font-display text-xl text-primary">{fmt(p.total)}</td>
                  <td className="px-5 py-4 text-right">
                    <Link to="/booking" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      {t("routes.book")} <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">{t("routes.note")}</p>
    </div>
  );
}
